// TODO: Many variables/components in here could be shared with nav/index
import React, { useMemo, useReducer } from "react";
import { graphql, useFragment } from "react-relay";
import { useLocation } from "react-router-dom";
import { CSSTransition } from "react-transition-group";

import { faChevronRight } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { Keyboard } from "@capacitor/keyboard";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import { noop, throttle } from "lodash";
import "tippy.js/animations/shift-away.css";

import { useShouldShowUpgradeLink } from "~/billing/utils";
import GlobalKeyboardListener from "~/common/GlobalKeyboardListener";
import { useBeaconSocket } from "~/lib/beacons";
import { inGoogleCalendar, inMSOutlook } from "~/lib/browserExtension";
import useMedia from "~/lib/hooks/useMedia";
import { dispatchAnalyticsTrack } from "~/lib/segment";
import { inZoomMeeting, isDesktopApp, isMacos, isNative } from "~/lib/utils";
import { ReactComponent as ActionItemsIconActive } from "~/nav/icons/action-items-filled.svg";
import { ReactComponent as ActionItemsIcon } from "~/nav/icons/action-items-outline.svg";
import { ReactComponent as AskFellowIconActive } from "~/nav/icons/ask-fellow-filled.svg";
import { ReactComponent as AskFellowIcon } from "~/nav/icons/ask-fellow-outline.svg";
import { CalendarOutline as MeetingsIcon, CalendarFilled as MeetingsIconActive } from "~/nav/icons/Calendar";
import { FeedbackContextProvider } from "~/nav/panels/Feedback";
import { AccountAvatar, Tooltip } from "~/ui";
import LoadingSpinner from "~/ui/loading/LoadingSpinner";

import { ActionItemsPanel } from "./panels/ActionItems";
import AskFellowPanel from "./panels/AskFellow";
import Meetings from "./panels/Meetings";
import ReturnToMeetingPrompt from "./ReturnToMeetingPrompt";
import UpgradeLink from "./UpgradeLink";

import type { CompanionModeNavbar_viewer$key } from "~/__generated__/CompanionModeNavbar_viewer.graphql";

import styles from "./nav.module.css";

const MOBILE_MAX_WIDTH = 735; // must match media queries used in nav.less
/** Mode in which we slide panels on top of content, instead of pushing the content to the side */
export const SLIDEOVER_MAX_WIDTH = 1150; // 1150 is very much eyeballed

/** If we define these manually, rather than using the defaults, we can use :local CSS module classnames */
const cssTransitionClassNames = {
	enter: styles.transitionOpenFrom,
	enterActive: styles.transitionOpenTo,
	exit: styles.transitionCloseFrom,
	exitActive: styles.transitionCloseTo,
};

/** The width, in pixels, of the main nav */
let navWidth = 68;
/** The width, in pixels, of the nav panel */
let panelWidth = 280;
// Ask Fellow panel is wider than other panels
let askFellowPanelWidth = 380;

export interface NavOption {
	/** The displayed name of the section */
	label?: string;
	/** The icon to display - should use `currentColor` as its colour */
	icon: {
		active: React.ReactNode;
		inactive: React.ReactNode;
	};
	/** The id for which panel this is  */
	panelKey: PanelKey;
	/** The panel to render when the button is active */
	panel: React.ReactNode;
	/** Optional custom CSS class for the icon wrapper when active */
	customActiveIconClass?: string;
	/** Optional custom CSS class for the label when active */
	customActiveLabelClass?: string;
}

const loadingSpinner = <LoadingSpinner dimmer={false} delay={300} />;

/** A button in the primary section of the nav – importantly a _button_, and not a link */
const NavButton = React.memo(function NavButton(
	props: NavOption & {
		/** Whether the button is active -- that is, whether its panel is open */
		active?: boolean;
		/** A className to be applied to a green "beacon" dot, calling users' attention to this button */
		beaconClass?: string;
		/** Optional custom class for the icon wrapper when active */
		customActiveIconClass?: string;
		/** Optional custom class for the label when active */
		customActiveLabelClass?: string;
		/** The function to call when the button is clicked */
		onSelect: (key: PanelKey) => void;
	},
) {
	const { panelKey, onSelect } = props;
	const doClick = React.useCallback(() => {
		onSelect(panelKey);
	}, [onSelect, panelKey]);
	return (
		<button
			className={styles.navButton}
			data-active={!!props.active}
			onClick={doClick}
			data-test-id={`nav-button--${panelKey}`}
		>
			<div
				className={cx(styles.navButtonIconWrapper, { [props.customActiveIconClass ?? ""]: props.active })}
				aria-hidden
			>
				{props.active ? props.icon.active : props.icon.inactive}
				{props?.beaconClass && <div className={props.beaconClass} />}
			</div>
			{props.label && (
				<span className={cx(styles.navButtonLabel, { [props.customActiveLabelClass ?? ""]: props.active })}>
					{props.label}
				</span>
			)}
		</button>
	);
});

type NavAction = PanelKey | "toggle" | "open" | "close";

const NAV_PANEL_DISPATCH_EVENT = "companion-mode-nav-panel-dispatch";

type AnalyticsEvent = { event: string; properties: object; options: object };

export class NavPanelDispatchEvent extends CustomEvent<{
	key: NavAction;
	doNotClose?: boolean;
	analyticsEvent?: AnalyticsEvent;
}> {
	constructor(key: NavAction, doNotClose?: boolean, analyticsEvent?: AnalyticsEvent) {
		super(NAV_PANEL_DISPATCH_EVENT, { detail: { key, doNotClose, analyticsEvent } });
	}
}

declare global {
	interface WindowEventMap {
		[NAV_PANEL_DISPATCH_EVENT]: NavPanelDispatchEvent;
	}
}

export function useCompanionModeNavDispatchPanel() {
	return React.useCallback((key: NavAction, doNotClose?: boolean, analyticsEvent?: AnalyticsEvent) => {
		window.dispatchEvent(new NavPanelDispatchEvent(key, doNotClose, analyticsEvent));
	}, []);
}

export function useNavPanelAboveContent(): boolean {
	return useMedia(`(max-width: ${SLIDEOVER_MAX_WIDTH}px)`);
}

/** The reducer function used to update the panel state */
function panelReducer(state: PanelState, action: NavAction): PanelState {
	switch (action) {
		case "toggle":
			return { ...state, open: !state.open };
		case "open":
			return { ...state, open: true };
		case "close":
			return { ...state, open: false };
		default: {
			if (state.open && action === state.key) {
				return { ...state, open: false };
			} else {
				return { open: true, key: action };
			}
		}
	}
}

const viewerFragment = graphql`
	fragment CompanionModeNavbar_viewer on Viewer {
		unreadBeacons
		user {
			id
		}
		account {
			name
			image: logo
		}
		...utils_useShouldShowUpgradeLink_viewer
		...UpgradeLink_viewer
		...MeetingsPanel_viewer # TODO: make the Agenda itself handle loading this
	}
`;

const ALL_PANEL_KEYS = ["meetings", "action-items", "ask-fellow"] as const;

export type PanelKey = (typeof ALL_PANEL_KEYS)[number];

interface PanelState {
	/* Whether a panel is expanded */
	open: boolean;
	/* Which panel is currently, or was last, open */
	key: PanelKey;
}

interface CompanionModeNavContextType {
	dispatchPanel: React.Dispatch<PanelKey | "toggle" | "open" | "close">;
	panel: PanelState;
}

export const CompanionModeNavContext = React.createContext<CompanionModeNavContextType>({
	dispatchPanel: noop,
	panel: { open: false, key: "meetings" },
});
CompanionModeNavContext.displayName = "CompanionModeNavContext";

export default function CompanionModeNavbar({ viewer: viewerKey }: { viewer: CompanionModeNavbar_viewer$key }) {
	const viewer = useFragment(viewerFragment, viewerKey);
	const showUpgradeLink = useShouldShowUpgradeLink(viewer);
	useBeaconSocket(viewer.user.id);

	const isMobile = useMedia(`(max-width: ${MOBILE_MAX_WIDTH}px)`);
	const panelAboveContent = useNavPanelAboveContent();
	const canHover = useMedia("(hover: hover)");

	const location = useLocation();

	const [keyboardUp, setKeyboardUp] = React.useState<boolean>(false);

	React.useEffect(() => {
		if (isNative) {
			Keyboard.addListener("keyboardWillHide", () => {
				setKeyboardUp(false);
			});
			Keyboard.addListener("keyboardWillShow", () => {
				setKeyboardUp(true);
			});
		}
	}, []);

	const [panel, dispatchPanel] = useReducer(panelReducer, { open: !isMobile, key: "meetings" });

	React.useLayoutEffect(() => {
		const { pathname } = window.location;
		// In mobile & tablet mode, the panel should automatically close whenever we navigate
		// Hopefully I haven't missed any case in which it should stay open
		if (panelAboveContent && !(inZoomMeeting && pathname == "/meetings/")) dispatchPanel("close");
	}, [location, panelAboveContent, dispatchPanel]);

	React.useEffect(() => {
		function onNavDispatchPanel(event: NavPanelDispatchEvent) {
			if (event.detail.doNotClose && panel.key === event.detail.key) return dispatchPanel("open");

			if (event.detail.analyticsEvent && event.detail.key !== panel.key) {
				dispatchAnalyticsTrack(
					event.detail.analyticsEvent.event,
					event.detail.analyticsEvent.properties,
					event.detail.analyticsEvent.options,
				);
			}

			dispatchPanel(event.detail.key);
		}

		window.addEventListener(NAV_PANEL_DISPATCH_EVENT, onNavDispatchPanel);
		return () => window.removeEventListener(NAV_PANEL_DISPATCH_EVENT, onNavDispatchPanel);
	}, [panel.key]);

	const [hoveringNear, setHoveringNear] = React.useState(false);
	const toggleButtonAlwaysVisible = (panelAboveContent || !canHover) && panel.open;

	// Wrapper for dispatchPanel to track analytics when opening ask-fellow
	const dispatchPanelWithAskFellowAnalytics = React.useCallback(
		(key: PanelKey | "toggle" | "open" | "close") => {
			if (panel.key !== key) {
				switch (key) {
					case "ask-fellow": {
						// keeping old event name "[Ask Copilot] Modal opened" on purpose
						dispatchAnalyticsTrack("[Ask Copilot] Modal opened", { source: "navbar" });
						break;
					}
					default: {
						dispatchAnalyticsTrack("[Navigation] Panel opened", {
							source: "navbar",
							panel: key,
							inCalendarWithExtension: inGoogleCalendar ? "google" : inMSOutlook ? "outlook" : undefined,
						});
						break;
					}
				}
			}
			dispatchPanel(key);
		},
		[dispatchPanel, panel],
	);

	const togglePanel = React.useCallback(
		() => dispatchPanelWithAskFellowAnalytics("toggle"),
		[dispatchPanelWithAskFellowAnalytics],
	);
	const closePanel = React.useCallback(
		() => dispatchPanelWithAskFellowAnalytics("close"),
		[dispatchPanelWithAskFellowAnalytics],
	);

	/* Whenever the mouse is near the panel (in Desktop mode), show the panel toggle. It's done
	 * this way as opposed to just having an invisible element and using `:hover` because the
	 * invisible element method would be in front of the cursor, preventing the user from
	 * clicking anything behind it, and getting occluded if anything was in front of it. This
	 * way is more manual, but better reflects what we want: it's based purely on the mouse's
	 * position, not on what element it's currently pointing at. */
	React.useLayoutEffect(() => {
		if (!toggleButtonAlwaysVisible && canHover && !isMobile) {
			const handleMouseMove = throttle((e: MouseEvent) => {
				let panelWidthCalc = panel.key === "ask-fellow" ? askFellowPanelWidth : panelWidth;
				const hoverThreshold = navWidth + (panel.open ? panelWidthCalc : 0) + 32;
				if (!hoveringNear && e.clientX <= hoverThreshold) {
					setHoveringNear(true);
				} else if (hoveringNear && e.clientX > hoverThreshold) {
					setHoveringNear(false);
				}
			}, 100);
			window.addEventListener("mousemove", handleMouseMove);

			const handleMouseOut = () => {
				if (hoveringNear) setHoveringNear(false);
			};
			document.documentElement.addEventListener("mouseleave", handleMouseOut);

			return () => {
				window.removeEventListener("mousemove", handleMouseMove);
				document.documentElement.removeEventListener("mouseleave", handleMouseOut);
			};
		} else {
			return void 0;
		}
	}, [panel.open, panel.key, hoveringNear, setHoveringNear, toggleButtonAlwaysVisible, canHover, isMobile]);

	/**
	 * In this state, the size of the app's main content area is reduced, so we
	 * use this to adjust media queries on the screen's width.
	 */
	const mainWidthIsReduced = panel.open && !panelAboveContent && !isMobile;
	React.useLayoutEffect(() => {
		document.documentElement.classList.toggle("Nav--ReducedMainWidth", mainWidthIsReduced);
		document.documentElement.classList.toggle("Nav--FullMainWidth", !mainWidthIsReduced);
	}, [mainWidthIsReduced]);

	React.useEffect(() => {
		if (panel.key === "ask-fellow" && panel.open) {
			document.documentElement.style.setProperty("--nav-panel-width", `${askFellowPanelWidth}px`);
		} else {
			document.documentElement.style.removeProperty("--nav-panel-width");
		}
	}, [panel.key, panel.open]);

	const openAskFellow = React.useCallback(() => {
		dispatchPanel("ask-fellow");
	}, [dispatchPanel]);

	const navOptions = useMemo<NavOption[]>(
		() => [
			{
				icon: {
					inactive: <MeetingsIcon />,
					active: <MeetingsIconActive />,
				},
				panelKey: "meetings",
				label: "Meetings",
				panel: (
					<React.Suspense fallback={loadingSpinner}>
						<Meetings viewer={viewer} resyncOnMount={false} visible={panel.open} isTourOngoing={false} />
					</React.Suspense>
				),
			},
			{
				icon: {
					inactive: <AskFellowIcon />,
					active: <AskFellowIconActive />,
				},
				panelKey: "ask-fellow",
				label: "Ask Fellow",
				panel: (
					<React.Suspense fallback={loadingSpinner}>
						<AskFellowPanel />
					</React.Suspense>
				),
				customActiveIconClass: styles.askFellowActiveWrapper,
				customActiveLabelClass: styles.askFellowActiveLabel,
			},
			{
				icon: {
					inactive: <ActionItemsIcon />,
					active: <ActionItemsIconActive />,
				},
				panelKey: "action-items",
				label: "Action items",
				panel: (
					<React.Suspense fallback={loadingSpinner}>
						<ActionItemsPanel />
					</React.Suspense>
				),
			},
		],
		[viewer, panel.open],
	);

	return (
		<nav
			className={cx(styles.outer, {
				[styles.keyboardIsUp]: keyboardUp,
				"mr-2": !isMobile && !panelAboveContent && panel.open,
			})}
		>
			<CompanionModeNavContext.Provider
				value={useMemo(
					() => ({ dispatchPanel: dispatchPanelWithAskFellowAnalytics, panel }),
					[panel, dispatchPanelWithAskFellowAnalytics],
				)}
			>
				<FeedbackContextProvider>
					<div className={cx(styles.main, "items-center", { [styles.desktopMode]: isDesktopApp && isMacos })}>
						{!isMobile && (
							<div className="flex justify-center mt-4">
								<Tooltip placement="right" content={viewer.account.name} delay={[200, 0]}>
									<AccountAvatar account={viewer.account} size="large" />
								</Tooltip>
							</div>
						)}
						{/* TODO: Open the pricing page in a new tab/window. Clicking "Upgrade now" breaks you out of Companion Mode. */}
						{!isMobile && showUpgradeLink && <UpgradeLink viewer={viewer} className={styles.upgradeButton} />}
						<ul className={styles.mainItemSection}>
							{navOptions.map(option => (
								<li key={option.panelKey}>
									<NavButton
										{...option}
										active={panel.open && panel.key === option.panelKey}
										onSelect={dispatchPanelWithAskFellowAnalytics}
										beaconClass={
											viewer.unreadBeacons.includes(`Navbar__${option.panelKey}`) ? styles.navButtonBeacon : ""
										}
									/>
								</li>
							))}
						</ul>
						<GlobalKeyboardListener sequence="mod+/" onEvent={openAskFellow} global />
					</div>
					<CSSTransition in={panel.open} timeout={400} classNames={cssTransitionClassNames}>
						<div
							data-tour-tooltip-target="side-panel"
							className={cx(styles.panelContainer, {
								[styles.open]: panel.open,
								[styles.aboveContent]: panelAboveContent && !isMobile,
								[styles.desktopMode]: isDesktopApp && isMacos,
							})}
						>
							{navOptions.find(option => option.panelKey === panel.key)?.panel}
							<ReturnToMeetingPrompt />
						</div>
					</CSSTransition>
					{isMobile ? (
						<CSSTransition in={panel.open} timeout={400} classNames={cssTransitionClassNames}>
							<button className={cx(styles.mobileCloseButton, { [styles.visible]: panel.open })} onClick={closePanel}>
								{/* Could replace this with finding the relevant panelKey in the items list and returning its label,
								but this is less expensive */}
								Close {panel.key.replace("-", " ")} panel
							</button>
						</CSSTransition>
					) : (
						<button
							className={cx(styles.toggleButton, {
								[styles.visible]: toggleButtonAlwaysVisible || hoveringNear,
								[styles.rotated]: panel.open,
							})}
							title={panel.open ? "Hide panel" : "Show panel"}
							onClick={togglePanel}
							data-test-id="collapse-panel-button"
						>
							<FontAwesomeIcon icon={faChevronRight} />
						</button>
					)}
					{isMobile && (
						<div className={cx(styles.panelDimmer, { [styles.visible]: panel.open })} onClick={closePanel} />
					)}
				</FeedbackContextProvider>
			</CompanionModeNavContext.Provider>
		</nav>
	);
}
